import React, {useRef, useState} from "react";
import {observer} from "mobx-react";
import GeneralDialog from "./GeneralDialog";
import {useStores} from "../../Hooks";
import {
    CircularProgress,
    Paper,
    Select,
    Table, TableBody, TableCell,
    TableContainer,
    TableHead, TableRow,
    TextField,
    Typography
} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Paginator from "../Shared/Paginator";
import {batchOptions, chartResolutionEnum} from "../../enums";
import ConditionalComponent from "../Shared/ConditionalComponent";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import Utility from "../../Utility";
import {CartesianGrid, Label, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import Button from "@mui/material/Button";
import DateRangeIcon from '@mui/icons-material/DateRange';
import sharedUtils from "../../serverClientShared/sharedUtils";
import {MobileDatePicker} from "@mui/x-date-pickers";
import dayjs from "dayjs";

const renderHeading = category => {
    switch (category) {
        case 'ClientIp':
            return "IPAddress"
        case 'Location':
            return "location"
        case 'StartTimeStamp':
            return "date"
        case 'Duration':
            return "duration"
        case 'Device':
            return "deviceType"
        default:
            return ""
    }
}

const PayPerClickAnalyticsModal = observer(() => {
    const {modalStore, generalStore, procedureStore} = useStores();
    const payPerClick = modalStore.modalData.payPerClick || {};
    const payPerClickId = payPerClick._id;
    const [title, setTitle] = useState(`${payPerClick.ProcedureName} (${generalStore.translate('inProject')}: ${payPerClick.ProjectName})`);
    const [menuIndex, setMenuIndex] = useState(0);
    const [data, setData] = useState([]);
    const [dataSelected, setDataSelected] = useState(chartResolutionEnum.MONTHLY);
    const [datePickerType, setDatePickerType] = useState(null);

    const containerRef = useRef(null);
    const {
        startDate,
        sortBy,
        sortDirection,
        payPerClickSessions,
        pageNumber,
        total,
        batchIndex,
        loading,
        dateFrom,
        dateTo
    } = generalStore.payPerClickAnalytics;

    const setPayPerClickData = (value, prop) => generalStore.payPerClickAnalytics[prop] = value;

    const saveDateFrom = moment => {
        if (!moment) {
            setDatePickerType(null);
            return
        }
        const newDate = new Date(moment);
        if (newDate.valueOf() >= new Date(dateTo).valueOf()) {
            modalStore.showErrorMessage(generalStore.translate('startDateCannotBeMoreRecentThanEndDate'))
        } else if (newDate.valueOf() > Date.now()) {
            modalStore.showErrorMessage(generalStore.translate('startDateCannotBeInTheFuture'));
        } else setPayPerClickData(newDate, 'dateFrom');
        setDatePickerType(null);
        loadChartData().then();
    }

    const saveDateTo = moment => {
        if (!moment) {
            setDatePickerType(null);
            return
        }
        const newDate = new Date(moment);
        if (newDate.valueOf() <= new Date(dateFrom).valueOf()) {
            modalStore.showErrorMessage(generalStore.translate('endDateCannotBeOlderThanStartDate'));
        } else if (newDate.valueOf() > Date.now()) {
            modalStore.showErrorMessage(generalStore.translate('endDateCannotBeInTheFuture'));
        } else setPayPerClickData(newDate, 'dateTo');
        setDatePickerType(null);
        loadChartData().then();
    }

    const loadAnalytics = async () => {
        setPayPerClickData(true, "loading");
        const success = await generalStore.getPayPerClickSessions(payPerClickId);
        if (!!success) {
            setPayPerClickData(false, "loading");
        } else {
            console.error("error loading sessions");
            modalStore.showErrorMessage(generalStore.translate('problemLoadingPayPerClickAnalytics'));
        }
    }

    const loadChartData = async () => {
        setPayPerClickData(true, "loading");
        const success = await generalStore.getPayPerClickSessionsChartData(payPerClickId);
        if (!!success) {
            switch (dataSelected) {
                case chartResolutionEnum.DAILY:
                    setDataToDaily();
                    break
                case chartResolutionEnum.WEEKLY:
                    setDataToWeekly();
                    break
                case chartResolutionEnum.MONTHLY:
                default:
                    setDataToMonthly();
                    break
            }
            setPayPerClickData(false, "loading");
        } else {
            console.error("error loading chart data");
            modalStore.showErrorMessage(generalStore.translate('problemLoadingPayPerClickAnalytics'));
        }
    }

    function setDataToWeekly() {
        setData(generalStore.payPerClickAnalytics.weeklyData);
        setDataSelected(chartResolutionEnum.WEEKLY);
    }

    function setDataToMonthly() {
        setData(generalStore.payPerClickAnalytics.monthlyData);
        setDataSelected(chartResolutionEnum.MONTHLY);
    }

    function setDataToDaily() {
        setData(generalStore.payPerClickAnalytics.dailyData);
        setDataSelected(chartResolutionEnum.DAILY);
    }

    function getButtonClass(buttonData) {
        return dataSelected === buttonData ? "selected" : ""
    }

    const renderAnalyticsSelection = () => {
        switch (menuIndex) {
            case 0:
                return renderAnalyticsTable();
            case 1:
                return renderAnalyticsChart();
            default:
                return
        }
    }

    const renderAnalyticsTable = () => {
        return (
            <Paper
                elevation={0}
                className="pay-per-click-table-container"
            >
                {renderTableFilters()}
                {renderTableMain()}
            </Paper>
        )
    }

    const renderAnalyticsChart = () => {
        return (
            <>
                {renderChartFilters()}
                {renderChartMain()}
            </>
        )
    }

    const handleChangeFilterSort = async (value, prop, reset = false) => {
        setPayPerClickData(value, prop);
        await loadAnalytics();
        if (!!reset) {
            setPayPerClickData(0, "pageNumber")
        }
    }

    const searchPrev = async () => await handleChangeFilterSort(pageNumber - 1, "pageNumber")

    const searchNext = async () => await handleChangeFilterSort(pageNumber + 1, "pageNumber")

    const getMin = () => pageNumber * batchOptions[batchIndex] + 1

    const getMax = () => Math.min((pageNumber + 1) * batchOptions[batchIndex], total)

    const handleSelectBatchNum = async event => {
        setPayPerClickData(event.target.value, "batchIndex");
        await handleChangeFilterSort(0, "pageNumber");
    }

    const renderTableFilters = () => {
        return (
            <div className="approved-filter-container">
                <div className="filter-dropdowns">
                    <FormControl>
                        <div className='text-field-label'>{generalStore.translate('date')}</div>
                        <Select
                            variant="outlined"
                            className='filter-select'
                            value={startDate}
                            onChange={async e => await handleChangeFilterSort(e.target.value, 'startDate', true)}
                            MenuProps={{
                                getContentAnchorEl: null,
                                anchorOrigin: {
                                    vertical: "bottom",
                                    horizontal: "center",
                                }
                            }}
                        >
                            <MenuItem value={0}>{generalStore.translate('today')}</MenuItem>
                            <MenuItem value={1}>{generalStore.translate('yesterday')}</MenuItem>
                            <MenuItem value={7}>{generalStore.translate('last7Days')}</MenuItem>
                            <MenuItem value={30}>{generalStore.translate('last30Days')}</MenuItem>
                            <MenuItem value={-1}>{generalStore.translate('all')}</MenuItem>
                        </Select>
                    </FormControl>
                </div>
                <div className="pagination-menu">
                    <Paginator
                        onPreviousClick={searchPrev}
                        onNextClick={searchNext}
                        currentMin={getMin()}
                        currentMax={getMax()}
                        total={total}
                    />
                    <TextField
                        value={batchIndex}
                        className="select-batch-num theme-input-field"
                        select
                        onChange={handleSelectBatchNum}
                        variant="outlined"
                    >
                        {batchOptions.map((option, i) => (
                            <MenuItem key={option} value={i} className="comments-batch-num">
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Typography
                        component="span"
                        variant="subtitle2"
                        color="textPrimary"
                    >
                        {generalStore.translate('perPage')}
                    </Typography>
                </div>
            </div>
        )
    }

    const renderTitleAndSortIcon = (category) => {

        const selectSort = async () => {
            if (sortBy === category) {
                setPayPerClickData(sortDirection * -1, "sortDirection")
            } else setPayPerClickData(category, "sortBy");
            await loadAnalytics();
        }

        return (
            <div
                className={`pay-per-click-sort-container${sortBy === category ? " selected" : ""}`}
                onClick={selectSort}
            >
                <div
                    className={sortBy === category ? "selected" : ""}>{generalStore.translate(renderHeading(category))}</div>
                <ConditionalComponent if={sortBy === category}>
                    {sortDirection === -1
                        ? <ArrowDownwardIcon/>
                        : <ArrowUpwardIcon/>
                    }
                </ConditionalComponent>
                <ConditionalComponent if={sortBy !== category}>
                    <div className="empty-box"/>
                </ConditionalComponent>
            </div>
        )
    }

    const renderTableMain = () => {
        return (
            <TableContainer component={"div"} className='pay-per-click-table-main'>
                <ConditionalComponent if={loading}>
                    <div className='loader-container'>
                        <CircularProgress color='primary' variant='indeterminate'/>
                    </div>
                </ConditionalComponent>
                <Table className='pay-per-click-table' aria-label="pay per click analytics table">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                {renderTitleAndSortIcon("ClientIp", true)}
                            </TableCell>
                            <TableCell>
                                {renderTitleAndSortIcon("Location", true)}
                            </TableCell>
                            <TableCell>
                                {renderTitleAndSortIcon("StartTimeStamp", true)}
                            </TableCell>
                            <TableCell>
                                {renderTitleAndSortIcon("Duration", true)}
                            </TableCell>
                            <TableCell>
                                {renderTitleAndSortIcon("Device", true)}
                            </TableCell>
                            <TableCell/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {renderTableRows()}
                    </TableBody>
                </Table>
            </TableContainer>
        )
    }

    const renderTableRows = () => {
        return payPerClickSessions.map((session, i) => {
            return (
                <TableRow
                    key={payPerClick._id + "row" + i}
                    className="task-row"
                >
                    <TableCell>{session.ClientIp}</TableCell>
                    <TableCell>{session.Location}</TableCell>
                    <TableCell>{Utility.renderDate_DDMMYYY(session.StartTimeStamp)}</TableCell>
                    <TableCell>{procedureStore.getSessionDuration(session)}</TableCell>
                    <TableCell align="left">{session.Device}</TableCell>
                    <TableCell className='link blue-link' onClick={() => openSessionPlayer(session)}>
                        {generalStore.translate('sessionReview')}
                    </TableCell>
                </TableRow>
            )
        });
    }

    const openSessionPlayer = session => {
        modalStore.modalData.session = {
            id: session._id,
            userName: generalStore.translate('guest'),
            procedureName: session.ProcedureName,
            numberOfSteps: session.NumberOfSteps,
            duration: sharedUtils.getSessionDurationMillis(session),
            date: Utility.renderFullDateTime(session.StartTimeStamp, generalStore.translate('dateLocale')),
            Device: session.Device,
            SessionType: session.SessionType,
            TaskRef: session.TaskRef,
            videoId: session.VideoFileId,
            modeName: session.ModeName,
            isTest: session.IsTest,
            score: session.Score,
            sessionRaw: session,
        };
        modalStore.isSessionPlayerModalOpen = true;
    }

    const renderChartFilters = () => {
        return (
            <div className="chart-filters-container">
                <div className='buttons-title-container'>
                    <div className="date-range-select-container">
                        <span className="date-select" onClick={() => setDatePickerType("dateFrom")}>
                            {Utility.renderDate_DDMMYYY(dateFrom)}
                        </span>
                        <span>
                            -
                        </span>
                        <span className="date-select" onClick={() => setDatePickerType("dateTo")}>
                            {Utility.renderDate_DDMMYYY(dateTo)}
                        </span>
                        <DateRangeIcon/>
                    </div>
                    <div className='buttons-container'>
                        <Button className={getButtonClass(chartResolutionEnum.MONTHLY)} variant="outlined"
                                onClick={setDataToMonthly}>{generalStore.translate('month')}</Button>
                        <Button className={getButtonClass(chartResolutionEnum.WEEKLY)} variant="outlined"
                                onClick={setDataToWeekly}>{generalStore.translate('week')}</Button>
                        <Button className={getButtonClass(chartResolutionEnum.DAILY)} variant="outlined"
                                onClick={setDataToDaily}>{generalStore.translate('day')}</Button>
                    </div>
                </div>
            </div>
        )
    }

    function renderChartMain() {
        return (
            <div className="chart-main-container">
                <ResponsiveContainer
                    width="95%"
                    height={containerRef.current ? containerRef.current.offsetHeight - 150 : 0}
                >
                    <LineChart
                        // width={containerRef.current ? containerRef.current.offsetWidth - 70 : 0}
                        // height={containerRef.current ? containerRef.current.offsetHeight - 150 : 0}
                        data={data}
                        margin={{
                            top: 35, right: 0, left: 20, bottom: 20,
                        }}
                    >
                        <Tooltip/>
                        <CartesianGrid strokeDasharray="3 2" vertical={false}/>
                        <XAxis dataKey="key" tickLine={false}/>
                        <YAxis>
                            <Label angle={-90} position='insideLeft'>
                                {generalStore.translate('numberOfSessions')}
                            </Label>
                        </YAxis>
                        <Line type="monotone" dataKey={generalStore.translate('sessions')} stroke="#2F80ED" dot={false}
                              strokeWidth={4}
                              horizontal={false}/>
                    </LineChart>
                </ResponsiveContainer>
            </div>
        );
    }

    const renderBody = () => {
        return (
            <div className='body-container' ref={containerRef}>
                <div className="analytics-menu">
                    <div
                        onClick={() => setMenuIndex(0)}
                        className={`menu-header${menuIndex === 0 ? " selected" : ""}`}
                    >
                        {generalStore.translate('detailedSessions')}
                    </div>
                    <div
                        onClick={() => setMenuIndex(1)}
                        className={`menu-header${menuIndex === 1 ? " selected" : ""}`}
                    >
                        {generalStore.translate('behavior')}
                    </div>
                </div>
                <div className="pay-per-click-sharing-container">
                    <div className="analytics-container pay-per-click-main">
                        {renderAnalyticsSelection()}
                    </div>
                </div>

                <MobileDatePicker
                    open={datePickerType === "dateFrom"}
                    sx={{display: 'none'}}
                    value={dayjs(dateFrom)}
                    onChange={saveDateFrom}
                    onClose={() => setDatePickerType(null)}
                    onError={console.log}
                    disableFuture
                    closeOnSelect
                    minDate={dayjs(payPerClick?.CreationDate || 0)}
                />
                <MobileDatePicker
                    open={datePickerType === "dateTo"}
                    sx={{display: 'none'}}
                    value={dayjs(dateTo)}
                    onChange={saveDateTo}
                    onClose={() => setDatePickerType(null)}
                    onError={console.log}
                    disableFuture
                    closeOnSelect
                    minDate={dayjs(payPerClick?.CreationDate || 0)}
                />
            </div>
        )

    }

    const onEnter = async () => {
        setTitle(`${payPerClick.ProcedureName} (${generalStore.translate('inProject')}: ${payPerClick.ProjectName})`);
        await Promise.all([loadAnalytics(), loadChartData()]);
    }

    return (
        <GeneralDialog
            title={title}
            onEnter={onEnter}
            openControl='isPayPerClickAnalyticsOpen'
            className='procedure-option-dialog pay-per-click-analytics'
            body={renderBody()}
            isConfirmDisabled={true}
        />
    );

});

export default PayPerClickAnalyticsModal;
